import { defineStore } from 'pinia';
import api from '@/api';
import { useToast } from 'vue-toastification';
const toast = useToast();
import { i18n } from '@/i18n';

function findLocationById(locations = [], id) {
  if (!id || !Array.isArray(locations)) return null;

  for (const location of locations) {
    if (Number(location.id) === Number(id)) {
      return location;
    }

    const child = findLocationById(location.children || [], id);
    if (child) {
      return child;
    }
  }

  return null;
}

export const useAppStore = defineStore('appStore', {
  state: () => {
    return {
      authStatus: false,
      token: null,
      username: '',
      userData: null,
      loginModal: false,
      registerModal: false,
      businessAccountModal: false,
      resetLoginPasswordModal: false,

      locations: [],
      cart: [],
      foodCart: [],
      selectedLocation: null,
      mallBrands: [],
      publicationCategories: [],
      mallCategories: [],
      mediaCategories: [],
      exhibitionCategories: [],
      accountCategories: [],
      reportTypes: [],
      setting: {},
      orderSessionId: null,
      foodOrderSessionId: null,
      publicationLiked: [],
      foodLiked: [],
      mallLiked: [],
      videoLiked: [],
      businessLiked: [],
      announcementLiked: [],
      subscriptionSettings: [],
      catalog: {
        isNew: false,
        file: '',
        download_count: null,
      },
      lastSeen: {
        publications: 0,
        exhibition_stores_count: 0,
        exhibition_products_count: 0,
        mall_stores_count: 0,
        mall_products_count: 0,
        videos_count: 0,
        announcements_count: 0,
        business_accounts_count: 0,
        payments_count: 0,
        publication_categories: [],
        business_account_categories: [],
        published_publications: 0,
        rejected_publications: 0,
      },
      modals: {
        reportModal: false,
        commentModal: false,
        rateModal: false,
        resetPasswordModal: false,
      },
    };
  },
  actions: {
    setState(state, payload) {
      this[state] = payload;

      if (state === 'locations') {
        this.syncSelectedLocationWithCurrentLanguage(payload);
      }
    },
    setAuth(payload) {
      this.setState('authStatus', true);
      this.setState('token', payload.token);
      this.setState('username', payload.username);
    },
    setOrderSessionId(payload) {
      this.setState('orderSessionId', payload);
      localStorage.setItem('orderSessionId', payload);
    },
    setFoodOrderSessionId(payload) {
      this.setState('foodOrderSessionId', payload);
      localStorage.setItem('foodOrderSessionId', payload);
    },
    setAuthOnLogin(payload) {
      this.setAuth(payload);
      localStorage.setItem('token', payload.token);
    },
    setLocation(payload) {
      this.setState('selectedLocation', payload);
      localStorage.setItem('selectedLocation', JSON.stringify(payload));
    },

    syncSelectedLocationWithCurrentLanguage(locationsPayload = null) {
      const locations = locationsPayload || this.locations || [];
      const savedLocation = JSON.parse(localStorage.getItem('selectedLocation')) || 1;

      const selectedLocationId =
        typeof savedLocation === 'object' ? savedLocation?.id : savedLocation;

      const translatedLocation = findLocationById(locations, selectedLocationId);

      if (!translatedLocation) return;

      this.selectedLocation = translatedLocation;
      localStorage.setItem('selectedLocation', JSON.stringify(translatedLocation));
    },

    showLogin() {
      this.setState('loginModal', true);
    },
    hideLogin() {
      this.setState('loginModal', false);
    },
    logoutUser() {
      this.getMallSessionOrderId();
      this.getExhibitionSessionOrderId();
      this.setState('username', '');
      this.setState('authStatus', false);
      this.setState('userData', {});
      localStorage.removeItem('token');
    },
    setCart(payload) {
      this.setState('cart', payload);
    },
    setFoodCart(payload) {
      this.setState('foodCart', payload);
    },
    deleteOrderSessionId() {
      this.setState('cart', []);
      this.setState('orderSessionId', null);
      localStorage.removeItem('orderSessionId');
    },
    deleteFoodOrderSessionId() {
      this.setState('foodCart', []);
      this.setState('foodOrderSessionId', null);
      localStorage.removeItem('foodOrderSessionId');
    },

    async getUserData() {
      const response = await api.get(`users/me/`);
      this.setState('userData', response.data);
      return true;
    },

    async getSessionCart() {
      const response = await api.get(`mall/order-sessions/${this.orderSessionId}/`);
      this.setCart(response.data);
      return true;
    },

    async getMallSessionOrderId() {
      const response = await api.post('mall/order-sessions/');
      this.setOrderSessionId(response.data.id);
    },
    async getExhibitionSessionOrderId() {
      const response = await api.post('exhibition/order-sessions/');
      this.setFoodOrderSessionId(response.data.id);
    },

    async updateCartItem(id, quantity) {
      const response = await api.patch(`mall/cart-items/${id}/`, { quantity: quantity });
      if (response.status === 200) {
        toast.info(`${i18n.global.t('warning.CART_UPDATED')}`);
        await this.getSessionCart();
      }
      return true;
    },
    async deleteCartItem(id) {
      const response = await api.delete(`mall/cart-items/${id}/`);
      if (response.status === 204) {
        toast.warning(`${i18n.global.t('warning.CART_REMOVED')}`);
        await this.getSessionCart();
      }
      return true;
    },
    async getSessionFoodCart() {
      const response = await api.get(`exhibition/order-sessions/${this.foodOrderSessionId}/`);
      this.setFoodCart(response.data);
      return true;
    },
    async updateFoodCartItem(id, quantity) {
      const response = await api.patch(`exhibition/cart-items/${id}/`, { quantity: quantity });
      if (response.status === 200) {
        toast.info(`${i18n.global.t('warning.CART_UPDATED')}`);
        await this.getSessionFoodCart();
      }
      return true;
    },
    async deleteFoodCartItem(id) {
      const response = await api.delete(`exhibition/cart-items/${id}/`);
      if (response.status === 204) {
        toast.warning(`${i18n.global.t('warning.CART_REMOVED')}`);
        await this.getSessionFoodCart();
      }
      return true;
    },
    async getLiked(type) {
      const dataType = {
        publications: { state: 'publicationLiked', url: 'publications/publications/favorites/' },
        mall: { state: 'mallLiked', url: 'mall/products/favorites/' },
        exhibition: { state: 'foodLiked', url: 'exhibition/products/favorites/' },
        announcements: { state: 'announcementLiked', url: 'posts/announcements/favorites/' },
        videos: { state: 'videoLiked', url: 'posts/videos/favorites/' },
        business: { state: 'businessLiked', url: 'posts/business_accounts/favorites/' },
      };
      try {
        const response = await api.get(dataType[type]['url']);
        this.setState(dataType[type]['state'], response.data.results);
      } catch (e) {
        console.log(e);
      }
    },
    async getCatalogue() {
      let cat = localStorage.getItem('catalog');
      try {
        const response = await api.get(`posts/catalogs/${this.getLocation.id}/`);
        this.catalog = response.data;
        this.catalog.file = this.catalog.file;
        this.catalog.download_count = this.catalog.download_count;
        if (cat !== this.catalog.file) {
          this.catalog.isNew = true;
          localStorage.setItem('catalog', this.catalog.file);
        }
      } catch (e) {
        console.log(e);
      }
    },
    async downloadCatalog() {
      try {
        const response = await api.post(`posts/catalogs/${this.getLocation.id}/`);
        this.catalog.download_count += 1;
      } catch (e) {
        console.log(e);
      }
    },
  },
  getters: {
    getLocation() {
      return JSON.parse(localStorage.getItem('selectedLocation')) || 1;
    },
    getToken() {
      return localStorage.getItem('token');
    },
    getOrderSessionId(state) {
      return state.orderSessionId || localStorage.getItem('orderSessionId');
    },
    getFoodOrderSessionId(state) {
      return state.foodOrderSessionId || localStorage.getItem('foodOrderSessionId');
    },

    getMallCategory(state) {
      return id => {
        console.log(state.mallCategories?.filter(item => console.log(item.id, id)));
        return state.mallCategories?.find(item => item.id == id);
      };
    },

    getCartById(state) {
      return id => {
        let cart = state.cart?.orders?.find(item => item.id === id);
        return cart ? cart : null;
      };
    },
    getCartItemById(state) {
      return (id, size) => {
        const allCartItems = state.cart?.orders?.flatMap(order => order.cart_items);

        let product;
        if (size) {
          product = allCartItems?.find(
            cartItem => cartItem?.product?.id === id && cartItem.size === size,
          );
        } else {
          product = allCartItems?.find(cartItem => cartItem?.product?.id === id);
        }
        // Use find to search for the product by ID
        // Return the product if found, otherwise return null
        return product ? product : null;
      };
    },
    getCart(state) {
      return state.cart;
    },
    getCartItemsCount(state) {
      let sum = 0;
      if (state?.cart?.orders) {
        sum = state?.cart?.orders?.length;
      }
      return sum;
    },

    getFoodCartById(state) {
      return id => {
        let cart = state.foodCart?.orders?.find(item => item.id === id);
        return cart ? cart : null;
      };
    },
    getFoodCartItemById(state) {
      return id => {
        const allCartItems = state.foodCart?.orders?.flatMap(order => order.cart_items);
        // Use find to search for the product by ID
        let product;
        product = allCartItems?.find(cartItem => cartItem?.product?.id === id);
        // Return the product if found, otherwise return null
        return product ? product : null;
      };
    },
    getFoodCartItemsCount(state) {
      let sum = 0;
      if (state?.foodCart?.orders) {
        sum = state?.foodCart?.orders?.length;
      }
      return sum;
    },
    getFoodCart(state) {
      return state.foodCart;
    },
    getLastSeen(state) {
      return state.lastSeen;
    },
    getMediaNewsLikedCount(state) {
      return state.videoLiked.length + state.announcementLiked.length;
    },
    getMediaLikedCount(state) {
      return state.videoLiked.length;
    },
    getNewsLikedCount(state) {
      return state.announcementLiked.length;
    },
    getFoodLikedCount(state) {
      return state.foodLiked.length;
    },
    getPublicationLikedCount(state) {
      return state.publicationLiked.length;
    },
    getBusinessLikedCount(state) {
      return state.businessLiked.length;
    },
    getMallLikedCount(state) {
      return state.mallLiked.length;
    },
    getLastSeenExhibitionCount(state) {
      return state.lastSeen.exhibition_stores_count + state.lastSeen.exhibition_products_count > 999
        ? '+999'
        : state.lastSeen.exhibition_stores_count + state.lastSeen.exhibition_products_count;
    },
    getLastSeenMallCount(state) {
      return state.lastSeen.mall_stores_count + state.lastSeen.mall_products_count > 999
        ? '+999'
        : state.lastSeen.mall_stores_count + state.lastSeen.mall_products_count;
    },
    getLastSeenMediaCount(state) {
      return state.lastSeen.videos_count + state.lastSeen.announcements_count > 999
        ? '+999'
        : state.lastSeen.videos_count + state.lastSeen.announcements_count;
    },
    getLastSeenMallProductsCount(state) {
      return state.lastSeen.mall_products_count > 999 ? '+999' : state.lastSeen.mall_products_count;
    },
    getLastSeenMallStoresCount(state) {
      return state.lastSeen.mall_stores_count > 999 ? '+999' : state.lastSeen.mall_stores_count;
    },
    getLastSeenExhibitionProductsCount(state) {
      return state.lastSeen.exhibition_products_count > 999
        ? '+999'
        : state.lastSeen.exhibition_products_count;
    },
    getLastSeenExhibitionStoresCount(state) {
      return state.lastSeen.exhibition_stores_count > 999
        ? '+999'
        : state.lastSeen.exhibition_stores_count;
    },
    getLastSeenPublicationsCount(state) {
      return state.lastSeen.publications > 999 ? '+999' : state.lastSeen.publications;
    },
    getLastSeenMyAcceptedPublicationsCount(state) {
      return state.lastSeen.published_publications > 999
        ? '+999'
        : state.lastSeen.published_publications;
    },
    getLastSeenRejectedPublicationsCount(state) {
      return state.lastSeen.rejected_publications > 999
        ? '+999'
        : state.lastSeen.rejected_publications;
    },
    getLastSeenVideosCount(state) {
      return state.lastSeen.videos_count > 999 ? '+999' : state.lastSeen.videos_count;
    },
    getLastSeenAnnouncementsCount(state) {
      return state.lastSeen.announcements_count > 999 ? '+999' : state.lastSeen.announcements_count;
    },
    getLastSeenBusinessAccountsCount(state) {
      return state.lastSeen.business_accounts_count > 999
        ? '+999'
        : state.lastSeen.business_accounts_count;
    },
    getSubscriptionSettings(state) {
      return state.subscriptionSettings;
    },
  },
  // other options...
});
